import { Controller } from "@hotwired/stimulus"
import { template } from "lodash"
import { parseNumber, formatNumber } from "./../helpers"

// Pola widoku identyfikowane przez data-atrybuty poza scope kontrolera:
//   - [data-advance-order-notice]  — komunikat "brak powiązanego zamówienia"
//   - [data-advance-save-button]   — przyciski zapisu (do blokowania)
export default class extends Controller {
  static outlets = ["invoice--tax-rate"]

  static targets = [
    "form",
    "toggleButton",
    "positionsBody",
    "rowTemplate",
    "total",
    "totalContainer",
    "issueDate",
  ]

  static values = {
    priceSetup: Object,
    autoOpen: { type: Boolean, default: false },
  }

  relatedOrderChangeHandler = null
  rowTemplate = null

  connect() {
    if (this.hasRowTemplateTarget) {
      this.rowTemplate = template(this.rowTemplateTarget.innerHTML)
    }

    // Powrót po błędzie walidacji — form jest już widoczny z pozycjami
    if (this.hasFormTarget && !this.formTarget.classList.contains("hidden")) {
      this.recalculateTotal()
    } else if (this.autoOpenValue) {
      this.add()
    }

    this.relatedOrderChangeHandler = (event) => {
      if (event.target.name === "invoice[invoice_ids][]") {
        this.onRelatedOrderChange()
      }
    }
    document.addEventListener("change", this.relatedOrderChangeHandler)

    // Ustal stan na starcie (np. gdy params[:from] pre-zaznacza zamówienie)
    this.onRelatedOrderChange()
  }

  disconnect() {
    document.removeEventListener("change", this.relatedOrderChangeHandler)
  }

  add() {
    if (!this.hasFormTarget) return

    this.formTarget.classList.remove("hidden")
    if (this.hasToggleButtonTarget) this.toggleButtonTarget.classList.add("hidden")

    this.syncIssueDateFromAdvance()
    if (this.rowCount() === 0) this.addRow()
    this.updateSaveBlocker()
  }

  addRow() {
    if (!this.rowTemplate || !this.hasPositionsBodyTarget) return
    const key = this.rowCount()
    const html = this.rowTemplate({ key })
    this.positionsBodyTarget.insertAdjacentHTML("beforeend", html)
    const newRow = this.positionsBodyTarget.lastElementChild
    newRow.dataset.rowIndex = key
    this.copyTaxFromMainForm(newRow)
    this.refreshTaxKindVisibility()
  }

  removeRow(event) {
    if (!confirm(event.currentTarget.getAttribute("data-confirm-message"))) return
    document.querySelector(`[data-row-index="${event.params.index}"]`)?.remove()
    this.reindexRows()
    this.recalculateTotal()
    this.refreshTaxKindVisibility()
  }

  // Cofamy datę zamówienia jeśli jest późniejsza niż zaliczka (porównanie stringów YYYY-MM-DD)
  syncIssueDate(event) {
    if (!this.hasIssueDateTarget) return
    const advanceDate = event.target.value
    if (advanceDate && this.issueDateTarget.value > advanceDate) {
      this.issueDateTarget.value = advanceDate
    }
  }

  onRelatedOrderChange() {
    const anyChecked = document.querySelectorAll('input[name="invoice[invoice_ids][]"]:checked').length > 0
    if (!this.hasFormTarget) {
      this.updateSaveBlocker()
      return
    }

    const formVisible = !this.formTarget.classList.contains("hidden")
    const formInputs = this.formTarget.querySelectorAll("input, select, textarea, button")

    if (anyChecked) {
      // Blokuj pola inline formularza — nie będą wysłane, backend to ignoruje
      formInputs.forEach(el => { el.disabled = true })
      this.formTarget.querySelectorAll("[data-add-row]").forEach(el => el.classList.add("hidden"))
      if (!formVisible && this.hasToggleButtonTarget) {
        this.toggleButtonTarget.classList.add("hidden")
      }
    } else {
      formInputs.forEach(el => { el.disabled = false })
      this.formTarget.querySelectorAll("[data-add-row]").forEach(el => el.classList.remove("hidden"))
      if (!formVisible && this.hasToggleButtonTarget) {
        this.toggleButtonTarget.classList.remove("hidden")
      }
    }

    this.updateSaveBlocker()
    this.refreshTaxKindVisibility()
  }

  // Wyłączenie pól inline zmienia zestaw stawek idących na serwer, a z niego invoice--tax-rate
  // liczy rodzaj sprzedaży np i podstawę zwolnienia. Bez przeliczenia na zaliczce zostaje wartość
  // wybrana dla porzuconego zamówienia inline, a serwer nie nadpisuje niepustego pola —
  // Ksef::InvoiceValidator odrzuca wtedy zapis.
  refreshTaxKindVisibility() {
    if (!this.hasInvoiceTaxRateOutlet) return

    const taxRate = this.invoiceTaxRateOutlet
    if (taxRate.hasNaTaxKindRadiosTarget) taxRate.updateNaKindVisibility()
    taxRate.updateExemptTaxVisibility()
  }

  toggleAdditional(event) {
    const row = event.target.closest("[data-row-index]")
    if (!row) return
    row.querySelector("[data-additional-fields]")?.classList.toggle("hidden")
    row.querySelector("[data-btn-more]")?.classList.toggle("hidden")
    row.querySelector("[data-btn-less]")?.classList.toggle("hidden")
  }

  syncIssueDateFromAdvance() {
    if (!this.hasIssueDateTarget) return
    const advanceDateInput = document.getElementById("invoice_issue_date")
    if (advanceDateInput?.value) this.issueDateTarget.value = advanceDateInput.value
  }

  rowCount() {
    if (!this.hasPositionsBodyTarget) return 0
    return this.positionsBodyTarget.querySelectorAll("[data-row-index]").length
  }

  reindexRows() {
    this.positionsBodyTarget.querySelectorAll("[data-row-index]").forEach((row, i) => {
      row.dataset.rowIndex = i
      row.querySelectorAll("[name]").forEach(el => {
        el.name = el.name.replace(/\[\d+\]/, `[${i}]`)
      })
      row.querySelectorAll("[id]").forEach(el => {
        el.id = el.id.replace(/_\d+_/, `_${i}_`)
      })
      row.setAttribute("data-invoice--position-calculator-position-index-value", `${i}_`)
      const removeBtn = row.querySelector("[data-invoice--inline-estimate-remove-index-param]")
      if (removeBtn) removeBtn.dataset.invoiceInlineEstimateRemoveIndexParam = i
    })
  }

  // Szukamy stawki z pozycji głównego formularza (wykluczamy pola inline estimate)
  copyTaxFromMainForm(row) {
    const taxSelect = row.querySelector("select[name*='[tax]']")
    if (!taxSelect) return

    const mainTaxEl = document.querySelector(
      "[id^='invoice_positions_attributes_'][id$='_tax']:not([id*='_new_estimate_'])"
    )
    const mainTax = mainTaxEl?.value
    if (mainTax && [...taxSelect.options].some(o => o.value === mainTax)) {
      taxSelect.value = mainTax
    }
  }

  recalculateTotal() {
    const total = this.inlineTotal()
    if (this.hasTotalTarget) {
      this.totalTarget.textContent = formatNumber(total, { ...this.priceSetupValue, app_digits: 2 })
    }
    if (this.hasTotalContainerTarget) {
      this.totalContainerTarget.classList.toggle("hidden", this.rowCount() === 0)
    }
    this.updateSaveBlocker()
  }

  inlineTotal() {
    if (!this.hasPositionsBodyTarget) return 0
    let gross = 0
    this.positionsBodyTarget.querySelectorAll("[data-row-index]").forEach(row => {
      const grossEl = row.querySelector("[name*='[total_price_gross]']")
      let rowGross = parseNumber(grossEl?.value || "0", this.priceSetupValue)

      const additionalFields = row.querySelector("[data-additional-fields]")
      if (additionalFields && !additionalFields.classList.contains("hidden")) {
        const discountPercentEl = row.querySelector("[name*='[discount_percent]']")
        if (discountPercentEl && !discountPercentEl.classList.contains("hidden") && discountPercentEl.value) {
          rowGross *= 1 - parseNumber(discountPercentEl.value, this.priceSetupValue) / 100
        }
        const discountEl = row.querySelector("[name*='[discount]']:not([name*='discount_percent'])")
        if (discountEl && !discountEl.classList.contains("hidden") && discountEl.value) {
          rowGross -= parseNumber(discountEl.value, this.priceSetupValue)
        }
      }

      gross += rowGross
    })
    return gross
  }

  // Stawki, które faktycznie pojadą na serwer — invoice--tax-rate liczy z nich „czy gdziekolwiek
  // jest np/zw". Ukryty albo wyłączony formularz (wskazano zamówienie checkboxem) nic nie wysyła.
  get submittableTaxFields() {
    if (!this.hasFormTarget || this.formTarget.classList.contains("hidden")) return []

    return [...this.taxFields()].filter((taxField) => !taxField.disabled)
  }

  taxFields() {
    return this.formTarget.querySelectorAll("select[name*='[_new_estimate][positions_attributes]'][name$='[tax]']")
  }

  // Aktualizuje opcje podatku we wszystkich polach tax inline estimate — wywoływana przez invoiceOutlet przy zmianie MOSS
  setTaxOptions(options) {
    if (!this.hasFormTarget) return
    this.taxFields().forEach(taxField => {
      const tmpValue = taxField.value
      taxField.replaceChildren(...options.map(o => o.cloneNode(true)))
      taxField.value = tmpValue
      if (taxField.value !== tmpValue) {
        taxField.value = taxField.options[0]?.value ?? ""
      }
    })
  }

  // Warunek: zaznaczone istniejące zamówienie LUB inline form ma pozycje z ceną
  updateSaveBlocker() {
    const anyOrderSelected = document.querySelectorAll('input[name="invoice[invoice_ids][]"]:checked').length > 0
    const satisfied = anyOrderSelected || this.inlineTotal() > 0

    document.querySelector("[data-advance-order-notice]")?.classList.toggle("hidden", satisfied)

    document.querySelectorAll("[data-advance-save-button]").forEach(btn => {
      btn.disabled = !satisfied
      btn.classList.toggle("opacity-50", !satisfied)
      btn.classList.toggle("pointer-events-none", !satisfied)
    })
  }
}
